'use client';

import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { saveAuthCache, getAuthCache, clearAuthCache } from '@/lib/database/indexedDB';

export type UserRole = 'super_admin' | 'secretaria' | 'center_admin' | 'company_admin' | 'technician' | 'radiologist' | 'cliente' | 'admin_cliente' | 'operador_tc' | 'secretaria_tc' | 'secretaria_dp' | 'gerencia_dp';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  isActive: boolean;
  centerId: string | null;
}

interface AuthContextValue {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  catalogAccess: string[];          // NEW: ['RX', 'TC'] or subset for cliente role
  catalogLoading: boolean;          // NEW
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  hasRole: (role: UserRole) => boolean;
  isSuperAdmin: () => boolean;
  isSecretaria: () => boolean;
  isCenterAdmin: () => boolean;
  isCliente: () => boolean;
  isDpTcRole: () => boolean;        // NEW: any of the 3 DP TC roles
  isAdminCliente: () => boolean;    // NEW: admin_cliente role specifically
  canAccessAdmin: () => boolean;
  refreshSession: () => Promise<void>;
  refreshCatalogAccess: () => Promise<void>; // NEW
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  catalogAccess: [],
  catalogLoading: false,
  signUp: async () => {},
  signIn: async () => {},
  signOut: async () => {},
  getCurrentUser: async () => null,
  isEmailVerified: () => false,
  getUserProfile: async () => null,
  hasRole: () => false,
  isSuperAdmin: () => false,
  isSecretaria: () => false,
  isCenterAdmin: () => false,
  isCliente: () => false,
  isDpTcRole: () => false,
  isAdminCliente: () => false,
  canAccessAdmin: () => false,
  refreshSession: async () => {},
  refreshCatalogAccess: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function buildProfileFromMeta(user: any): UserProfile | null {
  if (!user) return null;
  const metaRole =
    user.user_metadata?.role ||
    user.app_metadata?.role ||
    'secretaria';
  return {
    id: user.id,
    email: user.email ?? '',
    fullName: user.user_metadata?.full_name || user.email?.split('@')[0] || '',
    role: metaRole as UserRole,
    isActive: true,
    centerId: user.user_metadata?.center_id ?? null,
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [catalogAccess, setCatalogAccess] = useState<string[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Single stable client instance
  const supabase = createClient();

  // Lock: while true, the onAuthStateChange handler will skip state updates
  // to avoid racing with the initial getSession() + fetchProfile sequence.
  const initializingRef = useRef(true);

  // Track mounted state to prevent setState after unmount
  const mountedRef = useRef(true);

  // ── Load cached auth on mount (instant offline support) ──────────────────
  useEffect(() => {
    getAuthCache().then((cached) => {
      if (cached?.profile && loading) {
        // Pre-populate from cache so UI renders immediately
        setProfile(cached.profile as UserProfile);
        setCatalogAccess(cached.catalogAccess || []);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = useCallback(async (userId: string, userObj?: any, attempt = 0): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, role, is_active, center_id')
        .eq('id', userId)
        .single();

      if (error || !data) {
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
          return fetchProfile(userId, userObj, attempt + 1);
        }
        console.warn('[AuthContext] fetchProfile failed, using metadata fallback:', error?.message);
        return buildProfileFromMeta(userObj);
      }

      return {
        id: data.id,
        email: data.email,
        fullName: data.full_name,
        role: data.role as UserRole,
        isActive: data.is_active,
        centerId: data.center_id ?? null,
      };
    } catch (e: any) {
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        return fetchProfile(userId, userObj, attempt + 1);
      }
      console.warn('[AuthContext] fetchProfile exception, using metadata fallback:', e?.message);
      return buildProfileFromMeta(userObj);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch catalog access for cliente and DP TC role users
  const fetchCatalogAccess = useCallback(async (userObj?: any, profileObj?: UserProfile | null) => {
    // Resolve role: prefer DB profile (most authoritative), then JWT metadata
    const role =
      profileObj?.role ||
      userObj?.user_metadata?.role ||
      userObj?.app_metadata?.role ||
      '';
    // DP TC roles always have TC-only access — no DB lookup needed
    if (['admin_cliente', 'operador_tc', 'secretaria_tc', 'secretaria_dp', 'gerencia_dp', 'coordinador_tc'].includes(role)) {
      setCatalogAccess(['TC']);
      // Persist to IndexedDB
      if (profileObj) {
        await saveAuthCache({ profile: profileObj, catalogAccess: ['TC'] });
      }
      return;
    }
    if (role !== 'cliente') {
      setCatalogAccess([]);
      return;
    }
    setCatalogLoading(true);
    try {
      // Try RPC first (uses SECURITY DEFINER, no RLS issues)
      const { data: rpcData, error: rpcErr } = await supabase.rpc('get_my_catalog_access');
      if (!rpcErr && rpcData && rpcData.length > 0) {
        setCatalogAccess(rpcData || []);
        if (profileObj) await saveAuthCache({ profile: profileObj, catalogAccess: rpcData || [] });
        return;
      }

      // Fallback 1: find company by auth_user_id then get access rows
      const { data: companyData } = await supabase
        .from('companies')
        .select('id, center_type')
        .eq('auth_user_id', userObj?.id)
        .limit(1)
        .single();

      if (companyData?.id) {
        const { data: accessData } = await supabase
          .from('client_catalog_access')
          .select('catalog_type')
          .eq('company_id', companyData.id);

        if (accessData && accessData.length > 0) {
          const access = accessData.map((a: any) => a.catalog_type);
          setCatalogAccess(access);
          if (profileObj) await saveAuthCache({ profile: profileObj, catalogAccess: access });
          return;
        }

        // Fallback 2: use companies.center_type as the authoritative catalog assignment
        // This handles TC clients whose client_catalog_access row was never created
        if (companyData.center_type) {
          const centerType = (companyData.center_type as string).toUpperCase();
          if (centerType === 'TC' || centerType === 'RX') {
            setCatalogAccess([centerType]);
            if (profileObj) await saveAuthCache({ profile: profileObj, catalogAccess: [centerType] });
            return;
          }
        }
      }

      // No catalog found
      setCatalogAccess([]);
    } catch {
      setCatalogAccess([]);
    } finally {
      if (mountedRef.current) setCatalogLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    initializingRef.current = true;

    // ── Step 1: Subscribe FIRST so no events are missed ──────────────────
    // The handler is gated by initializingRef so it won't race with Step 2.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      // While the initial seed is running, ignore listener events.
      // They will be redundant — initSession already has the latest session.
      if (initializingRef.current) return;
      if (!mountedRef.current) return;

      console.log('[AuthContext] onAuthStateChange:', event, newSession?.user?.email ?? 'no user');

      if (newSession?.user) {
        // Validate the user by contacting the Supabase Auth server (secure)
        const { data: { user: verifiedUser }, error: userError } = await supabase.auth.getUser();
        if (userError || !verifiedUser) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setCatalogAccess([]);
          return;
        }
        // Synchronously update session + user so consumers see them immediately
        setSession(newSession);
        setUser(verifiedUser);

        // Fetch profile asynchronously — loading stays false (already resolved)
        const p = await fetchProfile(verifiedUser.id, verifiedUser);
        if (mountedRef.current) {
          setProfile(p);
          // Fetch catalog access for cliente users — pass profile for accurate role detection
          await fetchCatalogAccess(verifiedUser, p);
        }
      } else {
        // Signed out or session expired
        setSession(null);
        setUser(null);
        setProfile(null);
        setCatalogAccess([]);
      }
    });

    // ── Step 2: Seed initial state from the persisted session ─────────────
    const initSession = async () => {
      try {
        // Use getUser() instead of getSession() — authenticates data via Auth server
        const { data: { user: currentUser }, error } = await supabase.auth.getUser();

        if (error) {
          console.warn('[AuthContext] getUser error:', error.message);
        }

        if (!mountedRef.current) return;

        if (currentUser) {
          // Retrieve the local session for the token (needed by some SDK calls)
          const { data: { session: localSession } } = await supabase.auth.getSession();
          setSession(localSession);
          setUser(currentUser);

          const p = await fetchProfile(currentUser.id, currentUser);
          if (mountedRef.current) {
            setProfile(p);
            await fetchCatalogAccess(currentUser, p);
          }
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
          setCatalogAccess([]);
        }
      } catch (e) {
        console.warn('[AuthContext] initSession error:', e);
      } finally {
        if (mountedRef.current) {
          // Release the lock — from this point onAuthStateChange drives state
          initializingRef.current = false;
          setLoading(false);
        }
      }
    };

    initSession();

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          role: (metadata as any)?.role || 'secretaria',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    // After sign-in, refresh the session so the JWT picks up the latest
    // app_metadata (including role = 'super_admin' set by the DB migration).
    try {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session?.user && mountedRef.current) {
        setSession(refreshed.session);
        setUser(refreshed.session.user);
        const p = await fetchProfile(refreshed.session.user.id, refreshed.session.user);
        if (mountedRef.current) {
          setProfile(p);
          await fetchCatalogAccess(refreshed.session.user, p);
        }
      }
    } catch {
      // Non-fatal: the onAuthStateChange handler will update state anyway
    }

    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setCatalogAccess([]);
    // Clear local auth cache on sign out
    await clearAuthCache();
  };

  const getCurrentUser = async () => {
    const { data: { user: currentUser }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return currentUser;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const p = await fetchProfile(user.id, user);
    if (p) setProfile(p);
    return p;
  };

  const hasRole = (role: UserRole): boolean => {
    return profile?.role === role;
  };

  const isSuperAdmin = () => {
    if (profile?.role === 'super_admin') return true;
    if (user?.user_metadata?.role === 'super_admin') return true;
    if (user?.app_metadata?.role === 'super_admin') return true;
    return false;
  };

  const isSecretaria = () => {
    return profile?.role === 'secretaria' || user?.user_metadata?.role === 'secretaria';
  };

  const isCenterAdmin = () => {
    return (
      profile?.role === 'center_admin' ||
      profile?.role === 'company_admin' ||
      user?.user_metadata?.role === 'center_admin' ||
      user?.user_metadata?.role === 'company_admin'
    );
  };

  const isCliente = () => {
    if (profile?.role === 'cliente') return true;
    if (user?.user_metadata?.role === 'cliente') return true;
    if (user?.app_metadata?.role === 'cliente') return true;
    return false;
  };

  const isDpTcRole = () => {
    return ['admin_cliente', 'operador_tc', 'secretaria_tc', 'secretaria_dp', 'gerencia_dp', 'coordinador_tc'].includes(profile?.role);
  };

  const isAdminCliente = () => {
    return profile?.role === 'admin_cliente';
  };

  const canAccessAdmin = () => {
    return isSuperAdmin() || isCenterAdmin() || profile?.role === 'technician' || profile?.role === 'radiologist';
  };

  const refreshSession = async (): Promise<void> => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data?.session) {
        setSession(data.session);
        setUser(data.session.user ?? null);
        if (data.session.user) {
          const p = await fetchProfile(data.session.user.id, data.session.user);
          if (p && mountedRef.current) setProfile(p);
          await fetchCatalogAccess(data.session.user, p);
        }
      }
    } catch (e) {
      console.warn('[AuthContext] refreshSession error:', e);
    }
  };

  const refreshCatalogAccess = async (): Promise<void> => {
    if (user) {
      await fetchCatalogAccess(user, profile);
    }
  };

  const value: AuthContextValue = {
    user,
    session,
    profile,
    loading,
    catalogAccess,
    catalogLoading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    hasRole,
    isSuperAdmin,
    isSecretaria,
    isCenterAdmin,
    isCliente,
    isDpTcRole,
    isAdminCliente,
    canAccessAdmin,
    refreshSession,
    refreshCatalogAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
